import { Control, Controller, useController, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styles from "./UtilityEmailTemplateConfiguration.module.scss";
import ToggleSlider from "../../../../../common/components/xepp/toggle-slider/ToggleSlider";
import { EmailIcon } from "../../../../../assets/icons";
import { UtilityLogoUpload } from "./UtilityLogoUpload";
import { UtilityColorField } from "./UtilityColorField";
import { UtilityEmailPreview } from "./UtilityEmailPreview";
import { EmailTemplateFormValues } from "../../../../../model/bps-admin/email-template-configuration";

interface UtilityEmailTemplateConfigurationProps {
	control: Control<EmailTemplateFormValues>;
	companyName: string;
}

/**
 * Email branding of the utility, next to a preview of the email it produces. The form is
 * owned by the page, so it survives a tab switch and is saved with the rest of the page.
 */
export function UtilityEmailTemplateConfiguration({
	control,
	companyName,
}: Readonly<UtilityEmailTemplateConfigurationProps>) {
	const { t } = useTranslation("bpsAdmin");
	const values = useWatch({ control }) as EmailTemplateFormValues;
	const { field: logoToggle } = useController({ control, name: "logoEnabled" });
	const { field: bannerToggle } = useController({ control, name: "bannerEnabled" });

	// The elements that can be switched off — the colors always apply. The logo toggle covers
	// both logo images. The banner has no upload: the backend draws it from the colors below on
	// save.
	const toggleable = [
		{ key: "logo", toggle: logoToggle },
		{ key: "banner", toggle: bannerToggle },
	] as const;
	const onCount = toggleable.filter(item => item.toggle.value).length;

	return (
		<div className={styles.container}>
			<section className={styles.card}>
				<header className={styles.header}>
					<span className={styles.headerIcon}>
						<EmailIcon />
					</span>
					<div className={styles.headerText}>
						<h4 className={styles.title}>{t("utilitySetup.emailTemplate.title")}</h4>
						<p className={styles.meta}>
							{t("utilitySetup.emailTemplate.activeCount", { count: onCount, total: toggleable.length })}
						</p>
					</div>
					<span className={styles.toggleAllLabel}>{t("utilitySetup.emailTemplate.toggleAll")}</span>
					<ToggleSlider
						size="sm"
						isActive={onCount === toggleable.length}
						onChange={isOn => toggleable.forEach(item => item.toggle.onChange(isOn))}
					/>
				</header>

				{toggleable.map(item => (
					<div key={item.key} className={styles.element}>
						<div className={styles.elementHeader}>
							<div className={styles.elementText}>
								<span className={styles.elementName}>
									{t(`utilitySetup.emailTemplate.${item.key}.name`)}
								</span>
								<span className={styles.elementDescription}>
									{t(`utilitySetup.emailTemplate.${item.key}.description`)}
								</span>
							</div>
							<ToggleSlider size="sm" isActive={item.toggle.value} onChange={item.toggle.onChange} />
						</div>
						{item.key === "logo" && (
							<>
								<Controller
									control={control}
									name="logo"
									render={({ field }) => (
										<UtilityLogoUpload value={field.value} onChange={field.onChange} />
									)}
								/>
								<span className={styles.elementDescription}>
									{t("utilitySetup.emailTemplate.logoText")}
								</span>
								<Controller
									control={control}
									name="logoText"
									render={({ field }) => (
										<UtilityLogoUpload value={field.value} onChange={field.onChange} />
									)}
								/>
							</>
						)}
					</div>
				))}

				<div className={`${styles.element} ${styles.colors}`}>
					<Controller
						control={control}
						name="primaryColor"
						render={({ field }) => (
							<UtilityColorField
								label={t("utilitySetup.emailTemplate.primaryColor")}
								value={field.value}
								onChange={field.onChange}
							/>
						)}
					/>
					<Controller
						control={control}
						name="secondaryColor"
						render={({ field }) => (
							<UtilityColorField
								label={t("utilitySetup.emailTemplate.secondaryColor")}
								value={field.value}
								onChange={field.onChange}
							/>
						)}
					/>
					<Controller
						control={control}
						name="accentColor"
						render={({ field }) => (
							<UtilityColorField
								label={t("utilitySetup.emailTemplate.accentColor")}
								value={field.value}
								onChange={field.onChange}
							/>
						)}
					/>
					<Controller
						control={control}
						name="fontColor"
						render={({ field }) => (
							<UtilityColorField
								label={t("utilitySetup.emailTemplate.fontColor")}
								value={field.value}
								onChange={field.onChange}
							/>
						)}
					/>
				</div>
			</section>

			<div className={styles.previewColumn}>
				<UtilityEmailPreview values={values} companyName={companyName} />
			</div>
		</div>
	);
}
